import { useState, useEffect, useCallback } from 'react';
import { FileCheck, AlertTriangle, Plus, X, ChevronDown } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

type I9Status = 'section1_pending' | 'section2_pending' | 'e_verify_pending' | 'complete' | 'reverification_required';

interface I9Record {
  id: string;
  employee_id: string;
  employee_name: string;
  employee_email: string;
  hire_date: string;
  status: I9Status;
  section1_completed_at?: string;
  section2_completed_at?: string;
  work_authorization_expiry?: string;
  everify_case_number?: string;
  everify_result?: string;
  created_at: string;
}

interface ExpiringRecord extends I9Record {
  days_remaining: number;
}

interface IncompleteRecord {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  hire_date: string;
  department_name?: string;
  days_overdue: number;
}

interface Summary {
  total: number;
  complete: number;
  incomplete: number;
  expiring_soon: number;
}

// ─── Status Badge ─────────────────────────────────────────────

const STATUS_LABELS: Record<I9Status, string> = {
  section1_pending: 'Section 1 Pending',
  section2_pending: 'Section 2 Pending',
  e_verify_pending: 'E-Verify Pending',
  complete: 'Complete',
  reverification_required: 'Re-verification Required',
};

const STATUS_COLORS: Record<I9Status, string> = {
  section1_pending: 'bg-amber-500/20 text-amber-400',
  section2_pending: 'bg-blue-500/20 text-blue-400',
  e_verify_pending: 'bg-purple-500/20 text-purple-400',
  complete: 'bg-green-500/20 text-green-400',
  reverification_required: 'bg-red-500/20 text-red-400',
};

function StatusBadge({ status }: { status: I9Status }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

// ─── Section 2 Completion Modal ───────────────────────────────

const LIST_A_DOCS = ['US Passport', 'US Passport Card', 'Permanent Resident Card (Form I-551)', 'Employment Authorization Document (Form I-766)'];
const LIST_B_DOCS = ["Driver's License", 'State/Local ID Card', 'School ID with photo'];
const LIST_C_DOCS = ['Social Security Account Number Card', 'US birth certificate', 'Certification of Birth Abroad (FS-545)'];

interface Section2ModalProps {
  record: I9Record;
  onClose: () => void;
  onCompleted: () => void;
}

function Section2Modal({ record, onClose, onCompleted }: Section2ModalProps) {
  const [docList, setDocList] = useState<'A' | 'B_C'>('A');
  const [form, setForm] = useState({
    list_a_type: LIST_A_DOCS[0]!,
    list_b_type: LIST_B_DOCS[0]!,
    list_c_type: LIST_C_DOCS[0]!,
    document_number: '',
    issuing_authority: '',
    expiry_date: '',
    work_auth_expiry: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!form.document_number.trim()) { setError('Document number is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const documents = docList === 'A'
        ? [{ list: 'A', document_type: form.list_a_type, document_number: form.document_number, issuing_authority: form.issuing_authority, expiry_date: form.expiry_date || undefined }]
        : [
            { list: 'B', document_type: form.list_b_type, document_number: form.document_number, issuing_authority: form.issuing_authority },
            { list: 'C', document_type: form.list_c_type, document_number: form.document_number, issuing_authority: form.issuing_authority },
          ];
      const res = await fetch(`/api/people/i9/${record.id}/complete-section2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documents, work_authorization_expiry: form.work_auth_expiry || undefined }),
      });
      if (!res.ok) throw new Error('Failed to complete section 2');
      onCompleted();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-lg space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Complete Section 2 — {record.employee_name}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Document List</label>
          <div className="flex gap-2">
            <button onClick={() => setDocList('A')} className={`flex-1 py-2 rounded-lg text-xs ${docList === 'A' ? 'bg-amber-500/20 text-amber-400' : 'bg-surface-3 text-muted-foreground hover:text-foreground'}`}>List A (Identity + Work Auth)</button>
            <button onClick={() => setDocList('B_C')} className={`flex-1 py-2 rounded-lg text-xs ${docList === 'B_C' ? 'bg-amber-500/20 text-amber-400' : 'bg-surface-3 text-muted-foreground hover:text-foreground'}`}>List B + C</button>
          </div>
        </div>

        {docList === 'A' ? (
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Document Type (List A)</label>
            <select value={form.list_a_type} onChange={e => setForm(f => ({ ...f, list_a_type: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground">
              {LIST_A_DOCS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">List B (Identity)</label>
              <select value={form.list_b_type} onChange={e => setForm(f => ({ ...f, list_b_type: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground">
                {LIST_B_DOCS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">List C (Work Auth)</label>
              <select value={form.list_c_type} onChange={e => setForm(f => ({ ...f, list_c_type: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground">
                {LIST_C_DOCS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Document Number</label>
            <input value={form.document_number} onChange={e => setForm(f => ({ ...f, document_number: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Issuing Authority</label>
            <input value={form.issuing_authority} onChange={e => setForm(f => ({ ...f, issuing_authority: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Document Expiry (optional)</label>
            <input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Work Auth Expiry (optional)</label>
            <input type="date" value={form.work_auth_expiry} onChange={e => setForm(f => ({ ...f, work_auth_expiry: e.target.value }))} className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button onClick={() => void save()} disabled={saving} className="px-4 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Complete Section 2'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

type Tab = 'active' | 'expiring' | 'incomplete';

export function IVerifyPage() {
  const [records, setRecords] = useState<I9Record[]>([]);
  const [expiring, setExpiring] = useState<ExpiringRecord[]>([]);
  const [incomplete, setIncomplete] = useState<IncompleteRecord[]>([]);
  const [summary, setSummary] = useState<Summary>({ total: 0, complete: 0, incomplete: 0, expiring_soon: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('active');
  const [section2Record, setSection2Record] = useState<I9Record | null>(null);
  const [initiatingId, setInitiatingId] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, expiringRes, incompleteRes] = await Promise.all([
        fetch('/api/people/i9/list'),
        fetch('/api/people/i9/expiring'),
        fetch('/api/people/i9/incomplete'),
      ]);
      if (listRes.ok) {
        const data = await listRes.json() as { records: I9Record[]; summary: Summary };
        setRecords(data.records);
        setSummary(data.summary);
      }
      if (expiringRes.ok) {
        const data = await expiringRes.json() as { expiring: ExpiringRecord[] };
        setExpiring(data.expiring);
      }
      if (incompleteRes.ok) {
        const data = await incompleteRes.json() as { incomplete: IncompleteRecord[] };
        setIncomplete(data.incomplete);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function initiateI9(employeeId: string) {
    setInitiatingId(employeeId);
    try {
      const res = await fetch(`/api/people/i9/initiate/${employeeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (res.ok) await load();
    } catch { /* silent */ }
    finally { setInitiatingId(null); }
  }

  async function submitEVerify(recordId: string) {
    try {
      await fetch(`/api/people/i9/${recordId}/everify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      await load();
    } catch { /* silent */ }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <FileCheck className="w-5 h-5 text-amber-500" />
        <h1 className="text-xl font-bold text-foreground">I-9 / E-Verify</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Total I-9s</p>
          <p className="text-2xl font-bold text-foreground">{summary.total}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Complete</p>
          <p className="text-2xl font-bold text-green-400">{summary.complete}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Incomplete</p>
          <p className="text-2xl font-bold text-amber-400">{summary.incomplete}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Expiring in 90 days</p>
          <p className={`text-2xl font-bold ${summary.expiring_soon > 0 ? 'text-red-400' : 'text-foreground'}`}>{summary.expiring_soon}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1">
        {(['active', 'expiring', 'incomplete'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${tab === t ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'}`}>
            {t === 'active' ? 'Active I-9s' : t === 'expiring' ? `Expiring (${expiring.length})` : `Incomplete (${incomplete.length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : tab === 'active' ? (
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">Employee</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Hire Date</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">E-Verify</th>
                <th className="text-right px-5 py-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan={5} className="text-center px-5 py-8 text-muted-foreground">No I-9 records found</td></tr>
              ) : records.map(r => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-surface-3/50">
                  <td className="px-5 py-3">
                    <p className="font-medium text-foreground">{r.employee_name}</p>
                    <p className="text-muted-foreground">{r.employee_email}</p>
                  </td>
                  <td className="text-center px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="text-center px-4 py-3 text-muted-foreground">{new Date(r.hire_date).toLocaleDateString()}</td>
                  <td className="text-center px-4 py-3">
                    {r.everify_case_number ? (
                      <span className="text-purple-400 text-xs">{r.everify_case_number}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="text-right px-5 py-3">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenDropdown(openDropdown === r.id ? null : r.id)}
                        className="flex items-center gap-1 px-2.5 py-1 rounded border border-border text-muted-foreground hover:text-foreground text-xs"
                      >
                        Actions <ChevronDown className="w-3 h-3" />
                      </button>
                      {openDropdown === r.id && (
                        <div className="absolute right-0 top-full mt-1 bg-surface-2 border border-border rounded-lg shadow-xl z-10 min-w-[160px]">
                          {r.status === 'section2_pending' && (
                            <button
                              onClick={() => { setSection2Record(r); setOpenDropdown(null); }}
                              className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-3"
                            >
                              Complete Section 2
                            </button>
                          )}
                          {r.status === 'e_verify_pending' && !r.everify_case_number && (
                            <button
                              onClick={() => { void submitEVerify(r.id); setOpenDropdown(null); }}
                              className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-surface-3"
                            >
                              Submit E-Verify
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === 'expiring' ? (
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">Employee</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Work Auth Expiry</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Days Remaining</th>
                <th className="text-right px-5 py-3 font-medium text-muted-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {expiring.length === 0 ? (
                <tr><td colSpan={4} className="text-center px-5 py-8 text-muted-foreground">No work authorizations expiring within 90 days</td></tr>
              ) : expiring.map(r => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-surface-3/50">
                  <td className="px-5 py-3">
                    <p className="font-medium text-foreground">{r.employee_name}</p>
                  </td>
                  <td className="text-center px-4 py-3 text-muted-foreground">{r.work_authorization_expiry ? new Date(r.work_authorization_expiry).toLocaleDateString() : '—'}</td>
                  <td className="text-center px-4 py-3">
                    <span className={`font-semibold ${r.days_remaining < 30 ? 'text-red-400' : r.days_remaining < 60 ? 'text-amber-400' : 'text-foreground'}`}>
                      {r.days_remaining} days
                    </span>
                  </td>
                  <td className="text-right px-5 py-3">
                    <button
                      onClick={() => setSection2Record(r)}
                      className="px-2.5 py-1 rounded text-xs bg-amber-500/10 text-amber-500 hover:bg-amber-500/20"
                    >
                      Re-verify
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="glass rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3 font-medium text-muted-foreground">Employee</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Hire Date</th>
                <th className="text-center px-4 py-3 font-medium text-muted-foreground">Days Overdue</th>
                <th className="text-right px-5 py-3 font-medium text-muted-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {incomplete.length === 0 ? (
                <tr><td colSpan={4} className="text-center px-5 py-8 text-muted-foreground">All eligible employees have I-9 records</td></tr>
              ) : incomplete.map(emp => (
                <tr key={emp.id} className="border-b border-border/50 hover:bg-surface-3/50">
                  <td className="px-5 py-3">
                    <p className="font-medium text-foreground">{emp.first_name} {emp.last_name}</p>
                    <p className="text-muted-foreground">{emp.email}</p>
                  </td>
                  <td className="text-center px-4 py-3 text-muted-foreground">{new Date(emp.hire_date).toLocaleDateString()}</td>
                  <td className="text-center px-4 py-3">
                    <span className={`font-semibold flex items-center justify-center gap-1 ${emp.days_overdue > 3 ? 'text-red-400' : 'text-amber-400'}`}>
                      <AlertTriangle className="w-3 h-3" /> {emp.days_overdue}d
                    </span>
                  </td>
                  <td className="text-right px-5 py-3">
                    <button
                      onClick={() => void initiateI9(emp.id)}
                      disabled={initiatingId === emp.id}
                      className="flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 ml-auto disabled:opacity-50"
                    >
                      <Plus className="w-3 h-3" />
                      {initiatingId === emp.id ? 'Starting...' : 'Initiate I-9'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section2Record && (
        <Section2Modal
          record={section2Record}
          onClose={() => setSection2Record(null)}
          onCompleted={() => void load()}
        />
      )}

      {/* Dismiss open dropdown when clicking outside */}
      {openDropdown && (
        <div className="fixed inset-0 z-0" onClick={() => setOpenDropdown(null)} />
      )}
    </div>
  );
}
